import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Download,
  Upload,
  Database,
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  FileArchive,
  FileJson,
  Shield,
  Info,
  Mail,
  Clock,
  Send,
  XCircle,
  Calendar,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AdminLayout } from "./AdminLayout";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAY_NAMES = ["Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu", "Minggu"];

export default function AdminBackup() {
  const [isExportingJson, setIsExportingJson] = useState(false);
  const [isExportingZip, setIsExportingZip] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [photoJob, setPhotoJob] = useState(null); // {running, phase, percent, ...}
  const [photoBusy, setPhotoBusy] = useState(false);
  const [importMode, setImportMode] = useState("merge");
  const [importFile, setImportFile] = useState(null);
  const [report, setReport] = useState(null);
  const [importJob, setImportJob] = useState(null); // {running, phase, percent, ...}
  const fileInputRef = useRef(null);

  // Auto-backup schedule state
  const [schedule, setSchedule] = useState(null);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [runningNow, setRunningNow] = useState(false);
  const [history, setHistory] = useState([]);
  const [smtpPassword, setSmtpPassword] = useState(""); // only sent if non-empty

  // Reset all per-kind busy flags.
  const clearBusy = () => {
    setPhotoBusy(false);
    setIsExportingJson(false);
    setIsExportingZip(false);
  };

  // Poll the server-side backup job (shared by JSON / ZIP / Photos). The file is
  // built on disk in the background, so no single request hits the 120s proxy
  // timeout. When done, we stream the finished file from disk (fast).
  const pollJob = async () => {
    const token = localStorage.getItem("admin_token");
    try {
      const res = await axios.get(`${API}/admin/full-backup/photos/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPhotoJob(res.data);
      if (res.data.running) {
        setTimeout(pollJob, 1500);
      } else {
        clearBusy();
        if (res.data.phase === "done") {
          toast.success("Backup siap. Mengunduh…");
          await downloadPhotoBackup();
        } else if (res.data.phase === "error") {
          toast.error(`Backup gagal: ${res.data.error || "unknown"}`);
        }
      }
    } catch (err) {
      clearBusy();
      toast.error("Gagal memeriksa status backup.");
    }
  };

  // Start a backup job of a given kind: "json" | "zip" | "photos".
  const startJob = async (kind) => {
    const token = localStorage.getItem("admin_token");
    if (kind === "json") setIsExportingJson(true);
    else if (kind === "zip") setIsExportingZip(true);
    else setPhotoBusy(true);
    setPhotoJob({ running: true, phase: "gathering", percent: 0, downloaded: 0, total_assets: 0, kind });
    try {
      await axios.post(`${API}/admin/full-backup/photos/start?kind=${kind}`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Backup dimulai. Mohon tunggu hingga selesai.");
      setTimeout(pollJob, 1200);
    } catch (err) {
      clearBusy();
      toast.error(err?.response?.data?.detail || "Gagal memulai backup.");
    }
  };

  const doExport = (format) => startJob(format);
  const startPhotoBackup = () => startJob("photos");

  const downloadPhotoBackup = async () => {
    const token = localStorage.getItem("admin_token");
    try {
      const res = await axios.get(`${API}/admin/full-backup/photos/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
        timeout: 600000,
      });
      const blob = new Blob([res.data], { type: "application/zip" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const cd = res.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      a.download = m ? m[1] : "pdam_full_backup_photos.zip";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Backup + foto berhasil diunduh");
    } catch (err) {
      toast.error("Gagal mengunduh backup foto.");
    }
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const name = f.name.toLowerCase();
    if (!name.endsWith(".json") && !name.endsWith(".zip")) {
      toast.error("File harus .json atau .zip");
      e.target.value = "";
      return;
    }
    if (f.size > 200 * 1024 * 1024) {
      toast.error("File terlalu besar (maks 200 MB)");
      e.target.value = "";
      return;
    }
    setImportFile(f);
    setReport(null);
  };

  // Poll the background import job. Processing runs server-side so no single
  // request hits the 120s proxy timeout, even for large backups.
  const pollImport = async () => {
    const token = localStorage.getItem("admin_token");
    try {
      const res = await axios.get(`${API}/admin/full-backup/import/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setImportJob(res.data);
      if (res.data.running) {
        setTimeout(pollImport, 1500);
      } else {
        setIsImporting(false);
        if (res.data.phase === "done" && res.data.report) {
          setReport(res.data.report);
          toast.success(
            `Import selesai: ${res.data.report.total_inserted} dokumen ditambahkan, ${res.data.report.total_skipped} dilewati`
          );
        } else if (res.data.phase === "error") {
          toast.error(`Import gagal: ${res.data.error || "unknown"}`);
        }
      }
    } catch (err) {
      setIsImporting(false);
      toast.error("Gagal memeriksa status import.");
    }
  };

  const doImport = async () => {
    if (!importFile) {
      toast.error("Pilih file backup terlebih dahulu");
      return;
    }
    const warn =
      importMode === "replace"
        ? "PERINGATAN: Mode REPLACE akan MENGHAPUS semua data lama lalu menggantinya dengan data dari backup.\n\nApakah Anda yakin ingin melanjutkan?"
        : "Mode MERGE akan menambahkan data baru dan melewati data yang sudah ada.\n\nLanjutkan import?";
    if (!window.confirm(warn)) return;

    setIsImporting(true);
    setReport(null);
    setImportJob({ running: true, phase: "saving", percent: 0 });
    try {
      const token = localStorage.getItem("admin_token");
      const fd = new FormData();
      fd.append("file", importFile);
      await axios.post(
        `${API}/admin/full-backup/import/start?mode=${importMode}`,
        fd,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
          timeout: 600000,
        }
      );
      toast.success("File terkirim. Memproses import di latar belakang…");
      setTimeout(pollImport, 1200);
    } catch (err) {
      console.error(err);
      setIsImporting(false);
      const msg = err?.response?.data?.detail || "Gagal memulai import";
      toast.error(msg);
    }
  };

  const resetImport = () => {
    setImportFile(null);
    setReport(null);
    setImportJob(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // ----- Auto-Backup Schedule -----
  const fetchSchedule = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const { data } = await axios.get(`${API}/admin/backup-schedule`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSchedule(data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchHistory = async () => {
    try {
      const token = localStorage.getItem("admin_token");
      const { data } = await axios.get(`${API}/admin/backup-schedule/history`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setHistory(data.runs || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchSchedule();
    fetchHistory();
  }, []);

  const updateField = (key, value) => {
    setSchedule((s) => ({ ...s, [key]: value }));
  };
  const updateSmtp = (key, value) => {
    setSchedule((s) => ({ ...s, smtp: { ...(s.smtp || {}), [key]: value } }));
  };

  const saveSchedule = async () => {
    if (!schedule) return;
    setSavingSchedule(true);
    try {
      const token = localStorage.getItem("admin_token");
      const body = {
        enabled: !!schedule.enabled,
        interval: schedule.interval,
        hour: Number(schedule.hour),
        minute: Number(schedule.minute),
        day_of_week: Number(schedule.day_of_week),
        day_of_month: Number(schedule.day_of_month),
        retention_count: Number(schedule.retention_count),
        notify_push: !!schedule.notify_push,
        recipient_email: schedule.recipient_email || "",
        smtp: {
          host: schedule.smtp?.host || "",
          port: Number(schedule.smtp?.port || 587),
          username: schedule.smtp?.username || "",
          from_email: schedule.smtp?.from_email || "",
          use_tls: schedule.smtp?.use_tls !== false,
          // only send password if user typed something new
          ...(smtpPassword ? { password: smtpPassword } : {}),
        },
      };
      const { data } = await axios.put(`${API}/admin/backup-schedule`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSchedule(data);
      setSmtpPassword("");
      toast.success("Pengaturan jadwal disimpan");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal menyimpan jadwal");
    } finally {
      setSavingSchedule(false);
    }
  };

  const testEmail = async () => {
    setTestingEmail(true);
    try {
      // Save first if password just typed, so backend has it
      if (smtpPassword) await saveSchedule();
      const token = localStorage.getItem("admin_token");
      await axios.post(
        `${API}/admin/backup-schedule/test-email`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Email uji coba terkirim — cek inbox tujuan");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal kirim email uji");
    } finally {
      setTestingEmail(false);
    }
  };

  const runNow = async () => {
    if (!window.confirm("Jalankan backup sekarang dan kirim ke email tujuan?")) return;
    setRunningNow(true);
    try {
      const token = localStorage.getItem("admin_token");
      const { data } = await axios.post(
        `${API}/admin/backup-schedule/run-now`,
        {},
        { headers: { Authorization: `Bearer ${token}` }, timeout: 600000 }
      );
      toast.success(`Backup terkirim: ${data.filename || "OK"}`);
      fetchSchedule();
      fetchHistory();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal backup sekarang");
    } finally {
      setRunningNow(false);
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "-";
    try {
      return new Date(iso).toLocaleString("id-ID", {
        dateStyle: "medium",
        timeStyle: "short",
      });
    } catch {
      return iso;
    }
  };

  return (
    <AdminLayout title="Backup & Restore">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
      >
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center">
            <Database className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-heading font-bold text-slate-800">
              Backup & Restore Database
            </h1>
            <p className="text-sm text-slate-500">
              Export semua data (user, laporan, peta, pelanggan, transaksi, dll)
              untuk keperluan migrasi atau backup berkala.
            </p>
          </div>
        </div>

        {/* Info Banner */}
        <Card className="mb-6 border-2 border-blue-200 bg-blue-50/60">
          <CardContent className="p-4 flex gap-3">
            <Info className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
            <div className="text-sm text-blue-800 space-y-1">
              <p className="font-semibold">Yang ikut dibackup:</p>
              <p>
                Semua collection MongoDB — termasuk{" "}
                <b>user, password hash, pelanggan, master data, peta global,
                kode peta, transaksi, top-up, cicilan, daftar antar surat,
                laporan, pengaturan, template WA</b>, dst.
              </p>
              <p className="text-xs text-blue-700/80 pt-1">
                Dilewati: cache sesi, log notifikasi, dan langganan push (data
                sementara yang akan dibuat ulang otomatis).
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Export Section */}
        <Card className="mb-6">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <Download className="w-5 h-5 text-emerald-600" />
              Export (Unduh Backup)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-600 mb-4">
              Unduh seluruh database. Simpan file ini di tempat aman sebelum
              migrasi atau reinstall.
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-4 rounded-xl border border-emerald-200 bg-emerald-50/40">
                <div className="flex items-center gap-2 mb-2">
                  <FileJson className="w-5 h-5 text-emerald-600" />
                  <h4 className="font-semibold text-emerald-800">
                    JSON Tunggal
                  </h4>
                </div>
                <p className="text-xs text-emerald-700 mb-3">
                  1 file <code>.json</code> berisi semua collection. Mudah
                  dibaca & di-edit manual jika perlu.
                </p>
                <Button
                  onClick={() => doExport("json")}
                  disabled={isExportingJson || isExportingZip}
                  className="w-full gap-2 bg-emerald-600 hover:bg-emerald-700"
                  data-testid="export-json-btn"
                >
                  {isExportingJson ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Menyiapkan…
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4" />
                      Unduh JSON
                    </>
                  )}
                </Button>
              </div>

              <div className="p-4 rounded-xl border border-indigo-200 bg-indigo-50/40">
                <div className="flex items-center gap-2 mb-2">
                  <FileArchive className="w-5 h-5 text-indigo-600" />
                  <h4 className="font-semibold text-indigo-800">
                    ZIP (Direkomendasikan)
                  </h4>
                </div>
                <p className="text-xs text-indigo-700 mb-3">
                  Arsip <code>.zip</code> berisi 1 file JSON per collection +
                  <code> manifest.json</code>. Lebih kecil & rapi untuk DB
                  besar.
                </p>
                <Button
                  onClick={() => doExport("zip")}
                  disabled={isExportingJson || isExportingZip}
                  className="w-full gap-2 bg-indigo-600 hover:bg-indigo-700"
                  data-testid="export-zip-btn"
                >
                  {isExportingZip ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Menyiapkan…
                    </>
                  ) : (
                    <>
                      <Download className="w-4 h-4" />
                      Unduh ZIP
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Export progress bar (JSON / ZIP) */}
            {(isExportingJson || isExportingZip) && photoJob && photoJob.kind !== "photos" && (
              <div className="mt-4" data-testid="export-progress">
                <div className="flex items-center justify-between text-xs text-slate-600 mb-1">
                  <span className="font-semibold">
                    {photoJob.phase === "gathering" && "Mengumpulkan data database…"}
                    {photoJob.phase === "zipping" && "Menyusun file backup…"}
                    {photoJob.phase === "done" && "Selesai"}
                    {!["gathering", "zipping", "done"].includes(photoJob.phase) && "Memproses…"}
                  </span>
                  <span className="font-mono font-bold text-indigo-700">
                    {photoJob.percent || 0}%
                  </span>
                </div>
                <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
                  <div
                    className="h-full bg-indigo-600 transition-all"
                    style={{ width: `${photoJob.percent || 0}%` }}
                  />
                </div>
                <p className="text-[11px] text-slate-500 mt-1">
                  Proses berjalan di latar belakang — tanpa batas waktu. File otomatis terunduh saat selesai.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Backup + Photos (background job) */}
        <Card className="mb-6 border-2 border-purple-200 bg-gradient-to-br from-purple-50/40 to-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <FileArchive className="w-5 h-5 text-purple-600" />
              Backup Lengkap + Foto
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-600 mb-4">
              Backup ini mengunduh <b>semua data database + seluruh file foto</b> (foto rumah, bukti, dll) yang tersimpan di penyimpanan cloud, lalu membundelnya ke dalam satu file ZIP (folder <code>assets/</code>). Prosesnya berjalan di latar belakang dengan indikator persen &mdash; tanpa batas waktu, biarkan hingga selesai lalu file otomatis terunduh.
            </p>
            {photoJob && photoJob.kind === "photos" && (photoJob.running || photoBusy) && (
              <div className="mb-4" data-testid="photo-backup-progress">
                <div className="flex items-center justify-between text-xs text-slate-600 mb-1">
                  <span className="font-semibold capitalize">
                    {photoJob.phase === "gathering" && "Mengumpulkan data database…"}
                    {photoJob.phase === "scanning" && "Memindai foto…"}
                    {photoJob.phase === "downloading" && `Mengunduh foto ${photoJob.downloaded || 0}/${photoJob.total_assets || 0}…`}
                    {photoJob.phase === "zipping" && "Menyusun ZIP…"}
                    {photoJob.phase === "done" && "Selesai"}
                    {!["gathering","scanning","downloading","zipping","done"].includes(photoJob.phase) && "Memproses…"}
                  </span>
                  <span className="font-mono font-bold text-purple-700">{photoJob.percent || 0}%</span>
                </div>
                <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
                  <div
                    className="h-full bg-purple-600 transition-all"
                    style={{ width: `${photoJob.percent || 0}%` }}
                  />
                </div>
                {photoJob.failed > 0 && (
                  <p className="text-[11px] text-amber-600 mt-1">{photoJob.failed} foto gagal diunduh (dilewati).</p>
                )}
              </div>
            )}
            <Button
              onClick={startPhotoBackup}
              disabled={photoBusy}
              className="w-full gap-2 bg-purple-600 hover:bg-purple-700"
              data-testid="export-photos-btn"
            >
              {photoBusy ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Memproses backup foto…
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  Backup + Foto (ZIP)
                </>
              )}
            </Button>
            {photoJob && photoJob.kind === "photos" && photoJob.phase === "done" && !photoBusy && (
              <Button
                onClick={downloadPhotoBackup}
                variant="outline"
                className="w-full gap-2 mt-2 border-purple-300 text-purple-700"
                data-testid="redownload-photos-btn"
              >
                <Download className="w-4 h-4" /> Unduh ulang backup terakhir
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Import Section */}
        <Card className="mb-6 border-2 border-amber-200 bg-gradient-to-br from-amber-50/30 to-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <Upload className="w-5 h-5 text-amber-600" />
              Import (Restore Backup)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-2 items-start p-3 rounded-lg bg-amber-100/50 border border-amber-200">
              <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
              <p className="text-xs text-amber-800">
                Login ke server tujuan sebagai <b>admin</b>, lalu upload file
                backup di sini. Disarankan tes dengan mode <b>MERGE</b> terlebih
                dahulu.
              </p>
            </div>

            {/* File picker */}
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-2">
                File Backup (.json atau .zip)
              </label>
              <input
                ref={fileInputRef}
                type="file"
                accept=".json,.zip,application/json,application/zip"
                onChange={handleFileChange}
                disabled={isImporting}
                className="w-full text-sm file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-indigo-600 file:text-white file:font-medium hover:file:bg-indigo-700 disabled:opacity-50"
                data-testid="backup-file-input"
              />
              {importFile && (
                <p className="text-xs text-slate-600 mt-2">
                  Dipilih: <b>{importFile.name}</b> (
                  {(importFile.size / 1024 / 1024).toFixed(2)} MB)
                </p>
              )}
            </div>

            {/* Mode selector */}
            <div>
              <label className="text-sm font-medium text-slate-700 block mb-2">
                Mode Import
              </label>
              <div className="grid sm:grid-cols-2 gap-2">
                <label
                  className={`flex items-start gap-2 p-3 rounded-lg border-2 cursor-pointer transition ${
                    importMode === "merge"
                      ? "border-emerald-400 bg-emerald-50"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <input
                    type="radio"
                    name="import-mode"
                    value="merge"
                    checked={importMode === "merge"}
                    onChange={(e) => setImportMode(e.target.value)}
                    disabled={isImporting}
                    className="mt-1"
                    data-testid="import-mode-merge"
                  />
                  <div>
                    <p className="text-sm font-semibold text-slate-800">
                      MERGE (Aman)
                    </p>
                    <p className="text-xs text-slate-600">
                      Tambah data baru, lewati yang sudah ada (cocokkan via{" "}
                      <code>id</code>).
                    </p>
                  </div>
                </label>
                <label
                  className={`flex items-start gap-2 p-3 rounded-lg border-2 cursor-pointer transition ${
                    importMode === "replace"
                      ? "border-red-400 bg-red-50"
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <input
                    type="radio"
                    name="import-mode"
                    value="replace"
                    checked={importMode === "replace"}
                    onChange={(e) => setImportMode(e.target.value)}
                    disabled={isImporting}
                    className="mt-1"
                    data-testid="import-mode-replace"
                  />
                  <div>
                    <p className="text-sm font-semibold text-red-700">
                      REPLACE (Berbahaya)
                    </p>
                    <p className="text-xs text-slate-600">
                      Hapus semua data lama, ganti total dengan backup.
                    </p>
                  </div>
                </label>
              </div>
            </div>

            {/* Import progress (background job) */}
            {importJob && (importJob.running || importJob.phase === "importing" || importJob.phase === "parsing") && (
              <div data-testid="import-progress">
                <div className="flex items-center justify-between text-xs text-slate-600 mb-1">
                  <span className="font-semibold">
                    {importJob.phase === "saving" && "Mengirim file ke server…"}
                    {importJob.phase === "parsing" && "Membaca & memvalidasi backup…"}
                    {importJob.phase === "importing" &&
                      `Mengimpor ${importJob.current_collection || ""} (${importJob.processed_collections || 0}/${importJob.total_collections || 0})…`}
                    {!["saving", "parsing", "importing"].includes(importJob.phase) && "Memproses…"}
                  </span>
                  <span className="font-mono font-bold text-amber-700">{importJob.percent || 0}%</span>
                </div>
                <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
                  <div
                    className="h-full bg-amber-500 transition-all"
                    style={{ width: `${importJob.percent || 0}%` }}
                  />
                </div>
                <p className="text-[11px] text-slate-500 mt-1">
                  {(importJob.total_inserted || 0).toLocaleString("id-ID")} ditambahkan ·{" "}
                  {(importJob.total_skipped || 0).toLocaleString("id-ID")} dilewati. Proses berjalan di latar belakang — jangan tutup halaman.
                </p>
              </div>
            )}

            <div className="flex gap-2">
              <Button
                onClick={doImport}
                disabled={!importFile || isImporting}
                className={`gap-2 flex-1 ${
                  importMode === "replace"
                    ? "bg-red-600 hover:bg-red-700"
                    : "bg-emerald-600 hover:bg-emerald-700"
                }`}
                data-testid="run-import-btn"
              >
                {isImporting ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Mengimport…
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Jalankan Import ({importMode.toUpperCase()})
                  </>
                )}
              </Button>
              {(importFile || report) && (
                <Button
                  variant="outline"
                  onClick={resetImport}
                  disabled={isImporting}
                  data-testid="reset-import-btn"
                >
                  Reset
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* ============ AUTO-BACKUP via EMAIL ============ */}
        <Card className="mb-6 border-2 border-violet-200 bg-gradient-to-br from-violet-50/30 to-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <Mail className="w-5 h-5 text-violet-600" />
              Auto-Backup via Email (Terjadwal)
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            {!schedule ? (
              <div className="flex items-center justify-center h-20">
                <div className="spinner" />
              </div>
            ) : (
              <>
                {/* Enabled toggle + status */}
                <div className="flex items-center justify-between p-3 rounded-lg bg-white border border-slate-200">
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${schedule.enabled ? "bg-emerald-500 animate-pulse" : "bg-slate-300"}`} />
                    <div>
                      <p className="text-sm font-semibold text-slate-800">
                        Status: {schedule.enabled ? "Aktif" : "Nonaktif"}
                      </p>
                      <p className="text-xs text-slate-500">
                        Backup berikutnya: <b>{fmtDate(schedule.next_run_at)}</b>
                      </p>
                    </div>
                  </div>
                  <label className="inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={!!schedule.enabled}
                      onChange={(e) => updateField("enabled", e.target.checked)}
                      data-testid="schedule-enabled-toggle"
                    />
                    <div className="relative w-11 h-6 bg-slate-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-violet-600"></div>
                  </label>
                </div>

                {/* Schedule config */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700 block mb-1">
                      <Calendar className="w-4 h-4 inline mr-1" />
                      Interval
                    </label>
                    <select
                      value={schedule.interval}
                      onChange={(e) => updateField("interval", e.target.value)}
                      className="w-full h-10 rounded-lg border border-slate-300 px-3 text-sm bg-white"
                      data-testid="schedule-interval-select"
                    >
                      <option value="daily">Harian</option>
                      <option value="weekly">Mingguan</option>
                      <option value="monthly">Bulanan</option>
                    </select>
                  </div>

                  {schedule.interval === "weekly" && (
                    <div>
                      <label className="text-sm font-medium text-slate-700 block mb-1">Hari</label>
                      <select
                        value={schedule.day_of_week}
                        onChange={(e) => updateField("day_of_week", Number(e.target.value))}
                        className="w-full h-10 rounded-lg border border-slate-300 px-3 text-sm bg-white"
                        data-testid="schedule-dow-select"
                      >
                        {DAY_NAMES.map((d, i) => (
                          <option key={i} value={i}>{d}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {schedule.interval === "monthly" && (
                    <div>
                      <label className="text-sm font-medium text-slate-700 block mb-1">Tanggal (1-28)</label>
                      <Input
                        type="number"
                        min="1"
                        max="28"
                        value={schedule.day_of_month}
                        onChange={(e) => updateField("day_of_month", Number(e.target.value))}
                        className="h-10"
                        data-testid="schedule-dom-input"
                      />
                    </div>
                  )}

                  <div>
                    <label className="text-sm font-medium text-slate-700 block mb-1">
                      <Clock className="w-4 h-4 inline mr-1" />
                      Jam (0-23 WIB)
                    </label>
                    <Input
                      type="number"
                      min="0"
                      max="23"
                      value={schedule.hour}
                      onChange={(e) => updateField("hour", Number(e.target.value))}
                      className="h-10"
                      data-testid="schedule-hour-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 block mb-1">Menit (0-59)</label>
                    <Input
                      type="number"
                      min="0"
                      max="59"
                      value={schedule.minute}
                      onChange={(e) => updateField("minute", Number(e.target.value))}
                      className="h-10"
                      data-testid="schedule-minute-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 block mb-1">Simpan riwayat (1-1000)</label>
                    <Input
                      type="number"
                      min="1"
                      max="1000"
                      value={schedule.retention_count}
                      onChange={(e) => updateField("retention_count", Number(e.target.value))}
                      className="h-10"
                      data-testid="schedule-retention-input"
                    />
                    <p className="text-xs text-slate-500 mt-1">
                      Jumlah riwayat backup yang disimpan di sistem (email tetap tersimpan di inbox).
                    </p>
                  </div>
                </div>

                {/* Recipient + SMTP */}
                <div className="border-t border-slate-200 pt-4">
                  <h4 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
                    <Mail className="w-4 h-4 text-violet-600" />
                    Konfigurasi Email (SMTP)
                  </h4>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div className="sm:col-span-2">
                      <label className="text-sm font-medium text-slate-700 block mb-1">
                        Email Tujuan (yang menerima backup)
                      </label>
                      <Input
                        type="email"
                        placeholder="admin@anda.com"
                        value={schedule.recipient_email || ""}
                        onChange={(e) => updateField("recipient_email", e.target.value)}
                        className="h-10"
                        data-testid="schedule-recipient-input"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700 block mb-1">SMTP Host</label>
                      <Input
                        type="text"
                        placeholder="smtp.gmail.com"
                        value={schedule.smtp?.host || ""}
                        onChange={(e) => updateSmtp("host", e.target.value)}
                        className="h-10"
                        data-testid="smtp-host-input"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700 block mb-1">Port</label>
                      <Input
                        type="number"
                        placeholder="587"
                        value={schedule.smtp?.port || 587}
                        onChange={(e) => updateSmtp("port", Number(e.target.value))}
                        className="h-10"
                        data-testid="smtp-port-input"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700 block mb-1">Username (email pengirim)</label>
                      <Input
                        type="text"
                        placeholder="pengirim@gmail.com"
                        value={schedule.smtp?.username || ""}
                        onChange={(e) => updateSmtp("username", e.target.value)}
                        className="h-10"
                        data-testid="smtp-username-input"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-slate-700 block mb-1">
                        Password / App Password
                        {schedule.smtp?.password_set && !smtpPassword && (
                          <span className="ml-2 text-xs text-emerald-600">(sudah tersimpan)</span>
                        )}
                      </label>
                      <Input
                        type="password"
                        placeholder={schedule.smtp?.password_set ? "•••••• (biarkan kosong = tidak diubah)" : "Masukkan password"}
                        value={smtpPassword}
                        onChange={(e) => setSmtpPassword(e.target.value)}
                        className="h-10"
                        data-testid="smtp-password-input"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="text-sm font-medium text-slate-700 block mb-1">Email Pengirim (From)</label>
                      <Input
                        type="email"
                        placeholder="pengirim@gmail.com (boleh sama dengan username)"
                        value={schedule.smtp?.from_email || ""}
                        onChange={(e) => updateSmtp("from_email", e.target.value)}
                        className="h-10"
                        data-testid="smtp-from-input"
                      />
                    </div>
                    <label className="sm:col-span-2 flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        checked={schedule.smtp?.use_tls !== false}
                        onChange={(e) => updateSmtp("use_tls", e.target.checked)}
                        data-testid="smtp-tls-toggle"
                      />
                      Gunakan STARTTLS (port 587). Matikan untuk SSL langsung (port 465).
                    </label>
                  </div>

                  <div className="mt-3 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">
                    <b>Gmail tip:</b> aktifkan 2-Step Verification, lalu buat
                    <a className="underline mx-1" href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer">
                      App Password
                    </a>
                    (16 karakter). Gunakan App Password tsb, bukan password akun.
                  </div>
                </div>

                {/* Push notify toggle */}
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={!!schedule.notify_push}
                    onChange={(e) => updateField("notify_push", e.target.checked)}
                    data-testid="schedule-notify-toggle"
                  />
                  Kirim push notification ke admin saat backup sukses/gagal
                </label>

                {/* Action buttons */}
                <div className="flex flex-wrap gap-2">
                  <Button
                    onClick={saveSchedule}
                    disabled={savingSchedule}
                    className="gap-2 bg-violet-600 hover:bg-violet-700"
                    data-testid="save-schedule-btn"
                  >
                    {savingSchedule ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                    Simpan Pengaturan
                  </Button>
                  <Button
                    onClick={testEmail}
                    disabled={testingEmail || savingSchedule}
                    variant="outline"
                    className="gap-2"
                    data-testid="test-email-btn"
                  >
                    {testingEmail ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    Kirim Email Uji
                  </Button>
                  <Button
                    onClick={runNow}
                    disabled={runningNow || savingSchedule}
                    className="gap-2 bg-emerald-600 hover:bg-emerald-700"
                    data-testid="run-now-btn"
                  >
                    {runningNow ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                    Backup Sekarang ke Email
                  </Button>
                </div>

                {/* Last run status */}
                {schedule.last_run_at && (
                  <div className={`p-3 rounded-lg border text-sm ${
                    schedule.last_status === "success"
                      ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                      : "bg-red-50 border-red-200 text-red-800"
                  }`}>
                    <div className="flex items-center gap-2">
                      {schedule.last_status === "success" ? (
                        <CheckCircle className="w-4 h-4" />
                      ) : (
                        <XCircle className="w-4 h-4" />
                      )}
                      <b>Backup terakhir:</b> {fmtDate(schedule.last_run_at)} —{" "}
                      <span className="uppercase">{schedule.last_status}</span>
                    </div>
                    {schedule.last_error && (
                      <p className="mt-1 text-xs">Error: {schedule.last_error}</p>
                    )}
                  </div>
                )}

                {/* History */}
                {history.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-slate-800 mb-2">Riwayat Backup ({history.length})</h4>
                    <div className="overflow-x-auto border border-slate-200 rounded-lg">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="text-left p-2">Waktu</th>
                            <th className="text-left p-2">Trigger</th>
                            <th className="text-left p-2">Status</th>
                            <th className="text-left p-2">File</th>
                            <th className="text-right p-2">Ukuran</th>
                          </tr>
                        </thead>
                        <tbody>
                          {history.map((r) => (
                            <tr key={r.id} className="border-t border-slate-100">
                              <td className="p-2">{fmtDate(r.started_at)}</td>
                              <td className="p-2">{r.trigger}</td>
                              <td className="p-2">
                                <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-semibold uppercase ${
                                  r.status === "success" ? "bg-emerald-100 text-emerald-700" :
                                  r.status === "failed" ? "bg-red-100 text-red-700" :
                                  "bg-slate-100 text-slate-700"
                                }`}>{r.status}</span>
                              </td>
                              <td className="p-2 font-mono truncate max-w-[180px]">{r.filename || (r.error ? "-" : "-")}</td>
                              <td className="p-2 text-right">{r.size_bytes ? `${(r.size_bytes/1024/1024).toFixed(2)} MB` : "-"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Report */}
        {report && (
          <Card className="mb-6 border-2 border-emerald-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-heading flex items-center gap-2 text-emerald-700">
                <CheckCircle className="w-5 h-5" />
                Hasil Import
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                  <p className="text-xs text-emerald-700">Mode</p>
                  <p className="text-lg font-bold text-emerald-800 uppercase">
                    {report.mode}
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
                  <p className="text-xs text-blue-700">Ditambahkan</p>
                  <p className="text-lg font-bold text-blue-800">
                    {report.total_inserted.toLocaleString("id-ID")}
                  </p>
                </div>
                <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
                  <p className="text-xs text-slate-700">Dilewati</p>
                  <p className="text-lg font-bold text-slate-800">
                    {report.total_skipped.toLocaleString("id-ID")}
                  </p>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <th className="text-left p-2 font-semibold text-slate-600">
                        Collection
                      </th>
                      <th className="text-right p-2 font-semibold text-slate-600">
                        Total
                      </th>
                      <th className="text-right p-2 font-semibold text-slate-600">
                        Inserted
                      </th>
                      <th className="text-right p-2 font-semibold text-slate-600">
                        Skipped
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(report.collections).map(([name, info]) => (
                      <tr
                        key={name}
                        className="border-b border-slate-100 hover:bg-slate-50"
                      >
                        <td className="p-2 font-mono text-xs">{name}</td>
                        <td className="p-2 text-right">
                          {info.error
                            ? "-"
                            : (info.total ?? 0).toLocaleString("id-ID")}
                        </td>
                        <td className="p-2 text-right text-emerald-700">
                          {info.error
                            ? "-"
                            : (info.inserted ?? 0).toLocaleString("id-ID")}
                        </td>
                        <td className="p-2 text-right text-slate-600">
                          {info.error
                            ? info.error
                            : (info.skipped ?? 0).toLocaleString("id-ID")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tips */}
        <Card className="bg-gradient-to-br from-slate-50 to-white">
          <CardContent className="p-4">
            <h4 className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
              <Shield className="w-4 h-4 text-violet-600" />
              Tips Migrasi Aman
            </h4>
            <ul className="text-sm text-slate-600 space-y-1 list-disc list-inside">
              <li>
                Lakukan <b>Export ZIP</b> di server lama, simpan file backup di
                penyimpanan aman (Drive / lokal).
              </li>
              <li>
                Di server baru, login sebagai <b>admin</b>, buka halaman ini,
                lalu upload file backup.
              </li>
              <li>
                Mulai dengan mode <b>MERGE</b> untuk menghindari overwrite tak
                disengaja.
              </li>
              <li>
                Setelah import, refresh halaman dan verifikasi data user,
                pelanggan, dan laporan.
              </li>
              <li>
                Password user tetap berlaku — tidak perlu reset password
                massal.
              </li>
            </ul>
          </CardContent>
        </Card>
      </motion.div>
    </AdminLayout>
  );
}
